import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { caminhoInformeContribuicao, generateExpirationTimestamp, GeraHash, GeraToken } from './authorization';
import { DocumentDownloadhRequest, DocumentSearchRequest } from '../models/document-GED';
import { LocalStorageService } from './local-storage.service';
import JSEncrypt from 'jsencrypt';

declare function encryptToRsa(data: string, b64Modulus: string, b64Exponent: string): string;

@Injectable({
  providedIn: 'root'
})
export class HttpClientService {

  constructor(
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService
  ) { }

  async getInformeResgate(cpf: string, lastYear: number, token: string): Promise<HttpResponse<Blob>> {
    const url = `${environment.baseUrl}/apis/api-previdencia-informe-rendimentos/v1/InformeRendimentoResgate/File?cpf=${cpf}&ano=${lastYear}`;
    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpInforme}`
    });
    return await lastValueFrom(this.httpClient.get(url, { headers, responseType: 'blob', observe: 'response' }));
  }

  async redirectFacil(cpf: string): Promise<void> {
    const basePath = environment.baseUrlFacil;

    try {
      const publicKey: any = await firstValueFrom(
        this.httpClient.get(`${basePath}/ExternalAuthentication/GetPublicKey`)
      );

      this.authenticate(publicKey, cpf, basePath);

    } catch (error) {
      console.error('Erro ao obter chave pública:', error);
    }
  }

  private authenticate(publicKey: any, cpf: string, basePath: string): void {
    const inputData = this.getInputData(publicKey, cpf);
    const url = `${basePath}/ExternalAuthentication/Logon`;

    this.submitFormInNewTab(url, inputData);
  }

  private getInputData(publicKey: any, cpf: string): string {
    const authParams = {
      'TipoDeAcesso': '20',
      'WebLogin': (cpf ?? '').replace(/\D/g, '').padStart(11, '0'),
      'StartPage': '',
      'HideMenu': false
    };

    const serializedValue = JSON.stringify(authParams);

    return encryptToRsa(serializedValue, publicKey.Modulus, publicKey.Exponent);
  }

  private submitFormInNewTab(url: string, inputData: string): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.target = '_blank';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'inputData';
    input.value = inputData;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  async postExtratoSaude(cpf: string, lastYear: number): Promise<HttpResponse<Blob>> {
    const payloadToken = this.GetPayload(String(cpf));
    const tokenApi = (await this.GetTokenAPI(payloadToken));

    const url = `${environment.baseUrl}/apis/api-previdencia-informe-rendimentos/v1/Informe/extrato-saude?cpf=${cpf}&ano=${lastYear}`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${tokenApi}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpInforme}`
    });

    return await lastValueFrom(this.httpClient.post(url, null, { headers, responseType: 'blob', observe: 'response' }));
  }

  async postGerarLinkTokenEmail(token: string): Promise<any> {
    const tokenIR = await this.GetTokenIR(token);
    const url = `${environment.baseUrl}/apis/api-previdencia-informe-rendimentos/v1/DocumentosIRUsuario/GerarLinkAutenticado?featureId=${environment.featureId}`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${tokenIR}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpInforme}`
    });

    return await lastValueFrom(this.httpClient.post(url, null, { headers, observe: 'response' }));
  }

  async getInformeContribuicao(cpf: string, lastYear: number, currentYear: number): Promise<HttpResponse<Blob>> {
    const informe = caminhoInformeContribuicao(cpf, lastYear, currentYear)
    const url = `${environment.baseUrlCdn}/informes/${currentYear}/informes_contribuicao/${informe}`;

    return await lastValueFrom(this.httpClient.get(url, { responseType: 'blob', observe: 'response' }));
  }

  async getValidaAutenticacao(cpf: number, dataNascimento: string, token: string): Promise<any> {
    // Garante que a data está no formato yyyy-MM-dd
    let dataFormatada = dataNascimento;

    // Se a data vier no formato dd/MM/yyyy, converte para yyyy-MM-dd
    if (dataNascimento.includes('/')) {
      const partes = dataNascimento.split('/');
      if (partes.length === 3) {
        dataFormatada = `${partes[2]}-${partes[1]}-${partes[0]}`; // yyyy-MM-dd
      }
    }

    const url = `${environment.baseUrl}/apis/api-previdencia-informe-rendimentos/v1/Informe/validacao-autenticacao?Cpf=${cpf}&DataNascimento=${dataFormatada}`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpInforme}`
    });

    return await lastValueFrom(this.httpClient.get(url, { headers, observe: 'response' }));
  }

  async postDocumentSearch(paylod: DocumentSearchRequest): Promise<any> {
    const url = `${environment.baseUrl}/apis/softexpert/v1/document/search`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpSoftexpert}`
    });

    return await lastValueFrom(this.httpClient.post(url, paylod, { headers, observe: 'response' }));
  }

  async postInformeRendimentosGED(paylod: DocumentDownloadhRequest): Promise<any> {
    const url = `${environment.baseUrl}/apis/softexpert/v1/document/download`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpSoftexpert}`
    });

    return await lastValueFrom(this.httpClient.post(url, paylod, { headers, observe: 'response' }));
  }

  async getPlanoPrevidencia(cpf: string): Promise<any> {
    const payloadToken = this.GetPayload(String(cpf));
    const tokenApi = (await this.GetTokenAPI(payloadToken));

    const url = `${environment.baseUrl}/apis/api-cadastro-dados-cadastrais/v1/previdencia/Participante/planos/${cpf}`;

    // Step 5: Fazer a chamada com o token gerado
    const headers = new HttpHeaders({
      "Authorization": `Bearer ${tokenApi}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.ocpDadosCadastrais}`
    });

    return await lastValueFrom(this.httpClient.get(url, { headers, observe: 'response' }));
  }


  async postScafPlusLogin(cpf: number): Promise<any> {
    //Step 1 gerar tokenDefault
    const token = await GeraToken(this.getObjTokenSSO(cpf), environment.secretPortalScaf);
    const tokenSSOScafPlus = await this.GetTokenScafPlus(token);

    const url = `${environment.baseUrlScaf}/apis/api-portal-restrito-sso/v1/Scafplus/Login`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpScaf}`
    });

    const body = `"${tokenSSOScafPlus}"`;

    return await lastValueFrom(this.httpClient.post(url, body, { headers, observe: 'response' }));
  }

  private async getAuthtenticationScafPlus(token: string): Promise<HttpResponse<any>> {
    const url = `${environment.baseUrlScaf}/apis/api-portal-restrito-sso/v1/Scafplus/Authenticate`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Ocp-Apim-Subscription-Key": `${environment.oacpScaf}`
    });

    try {
      const response = await lastValueFrom(this.httpClient.get<any>(url, { headers, observe: 'response' }));
      return response;
    } catch (error) {
      console.error('Erro ao obter autorização:', error);
      throw error;
    }
  }

  private GetPayload(cpf: any): any {
    const payload = {
      "idSecret": 0,
      "cpf": cpf,
      "exp": generateExpirationTimestamp(1) // Expira em 1 minuto
    };
    return payload;
  }

  private async getAuthorization(inHash: string, token: string): Promise<HttpResponse<any>> {
    const url = `${environment.baseUrl}/apis/api-portal-restrito-sso/v2/v2/Authentication/?hash=${inHash}`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`
    });

    try {
      const response = await lastValueFrom(this.httpClient.get<any>(url, { headers, observe: 'response' }));
      return response;
    } catch (error) {
      console.error('Erro ao obter autorização:', error); ''
      throw error;
    }
  }

  private async GetTokenIR(payload: any): Promise<any> {
    try {
      // //Step 1 gerar tokenDefault e hash
      // const tokenBase = await GeraToken(this.getObjTokenDefault(), environment.secretPortal);

      // const hash = GeraHash();

      // //Step 2 gerar autorization
      // const autorization = (await this.getAuthorization(hash, tokenBase)).body;

      // //Step 3 gerar token final
      // payload.idSecret = autorization?.secret?.id;

      const tokenApi = (await GeraToken(payload, environment.secretIR));

      return tokenApi;

    } catch (error) {
      return error;
    }
  }

  private async GetTokenAPI(payload: any): Promise<any> {
    try {
      //Step 1 gerar tokenDefault e hash
      const tokenBase = await GeraToken(this.getObjTokenDefault(), environment.secretPortal);

      const hash = GeraHash();

      //Step 2 gerar autorization
      const autorization = (await this.getAuthorization(hash, tokenBase)).body;

      //Step 3 gerar token final
      payload.idSecret = autorization?.secret?.id;

      const tokenApi = (await GeraToken(payload, autorization?.secret.value));

      return tokenApi;

    } catch (error) {
      return error;
    }
  }

  private getObjTokenDefault(): any {

    const timeStamp = generateExpirationTimestamp(1); // Um minuto
    const obj = {
      "exp": timeStamp
    };

    return obj;
  }

  private getObjTokenSSO(cpf: number): any {
    const timeStampDateTime = generateExpirationTimestamp(2);
    const timeStampExpiration = generateExpirationTimestamp(20);
    const obj = {
      "exp": timeStampExpiration,
      "datetime": timeStampDateTime,
      "cpf": cpf
    };

    return obj;
  }

  private async GetTokenScafPlus(token: any): Promise<any> {
    try {

      //Step 1 gerar autorization
      const autorization = (await this.getAuthtenticationScafPlus(token)).body;

      //Step 2 gerar token final
      const tokenApi = autorization?.access_Token;

      return tokenApi;

    } catch (error) {
      return error;
    }
  }

  // Valida token enviado via SMS/Email
  async validaToken(data: any): Promise<HttpResponse<any>> {
    const url = `${environment.baseUrl}/apis/api-notificacao/v1/notificacao/validar`;
    const tokenApi = this.localStorageService.get("bearerToken");
    const headers = new HttpHeaders({
      "Authorization": `${tokenApi}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.notificationsubscriptionkey}`
    });
    return lastValueFrom(this.httpClient.post<any>(url, data, { headers, observe: 'response' }));
  }

  // Busca dados de contato (nome, telefone, email) com CPF e data de nascimento
  async getDadosContato(cpf: string, dataNascimento: string): Promise<HttpResponse<any>> {
    const url = `${environment.baseUrl}/apis/api-previdencia-informe-rendimentos/v1/Informe/dado-contato`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpInforme}`
    });

    // Monta os parâmetros da query string
    const params = `?Cpf=${cpf}&DataNascimento=${dataNascimento}`;

    return await lastValueFrom(
      this.httpClient.get<any>(url + params, { headers, observe: 'response' })
    ).catch(error => {
      console.error('Erro ao buscar dados de contato:', error);
      throw error;
    });
  }
}
